import React, { useState, useEffect } from 'react';
import { IntegrationRequest } from '../../lib/tenant-api';
import Modal from '../Modal';
import type { IntegrationConfigProps } from './types';

export const GoogleDriveConfig: React.FC<IntegrationConfigProps> = ({
  isOpen,
  onClose,
  integration,
  createIntegrationMutation,
  updateIntegrationMutation
}) => {
  const [formData, setFormData] = useState({
    folder_id: '',
    is_enabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!integration;

  useEffect(() => {
    if (integration) {
      setFormData({
        folder_id: integration.config.folder_id || '',
        is_enabled: integration.is_enabled,
      });
    } else {
      setFormData({
        folder_id: '',
        is_enabled: true,
      });
    }
    setErrors({});
  }, [integration, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const integrationData: IntegrationRequest = {
      integration_type: integration?.integration_type || 'google_drive',
      name: integration?.name || 'Google Drive',
      config: {
        folder_id: formData.folder_id
      },
      is_enabled: formData.is_enabled,
    };

    if (isEditing && integration) {
      updateIntegrationMutation.mutate({
        id: integration.id,
        data: integrationData,
      });
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      return newData;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const isLoading = createIntegrationMutation.isLoading || updateIntegrationMutation.isLoading;
  return <Modal
    isOpen={isOpen}
    onClose={handleClose}
    title="Update Google Drive Integration"
  >
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Integration Name */}
      <div>
        <label htmlFor="folder_id" className="block text-sm font-medium text-gray-700 mb-2">
          Folder ID
        </label>
        <input
          type="text"
          id="folder_id"
          value={formData.folder_id}
          onChange={(e) => handleInputChange('folder_id', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="Enter Google Drive folder ID"
          disabled={isLoading}
        />
        {errors.folder_id && <p className="mt-1 text-sm text-red-600">{errors.folder_id}</p>}
      </div>

      <div className="bg-indigo-50 border border-indigo-200  p-4">
        <h4 className="text-sm font-medium text-indigo-800 mb-2">
          How to get your Google Drive folder ID
        </h4>

        <ol className="text-sm text-indigo-700 word-break break-all">
          The folder ID can be found in the URL of your Google Drive folder, for example: https://drive.google.com/drive/folders/1A2B3C4D5E6F7G8H9I0JKLMNOPQRSTUVWXYZ.

          <br /> <br />

          The folder ID is the last part of the URL, which is 1A2B3C4D5E6F7G8H9I0JKLMNOPQRSTUVWXYZ.
        </ol>

      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating...
            </>
          ) : (
            'Update Integration'
          )}
        </button>
      </div>
    </form>
  </Modal>

}
